import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useMutation } from '@apollo/react-hooks'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  CloudUpload,
  X,
  Folder,
  FolderOpen,
  LoaderCircle,
  Upload,
} from 'lucide-react'
import FileTypeIcon from '../../../components/file-manager/FileIcon'
import { useTranslation } from 'react-i18next'
import { toastr } from 'react-redux-toastr'
import { cn } from '@/lib/utils'
import * as Mutation from '../../../api/gql/mutation'
import { formatBytes } from '../../../utils/formatters'
import { useUpload } from '../../../components/file-manager/hooks/useUpload'
import ProgressUpload from '../../../components/file-manager/progress'
import { UploadProgressBars } from '../../../components/file-manager/progress'
import {
  FolderUploadData,
  TreeNode,
  readDroppedItems,
  readInputFolder,
  formatFolderTree,
  hasDirectoryEntries,
  isFolderUploadSupported,
} from '../utils/folder-upload'

interface DocUploadModalProps {
  isOpen: boolean
  onClose: () => void
  onComplete: () => void
  folderId?: string | null
  documentId?: string | null
  initialFolderData?: FolderUploadData | null
  initialFiles?: File[] | null
}

interface FileWithMeta {
  file: File
  id: string
  name: string
  comment: string
}

type UploadMode = 'files' | 'folder'
type UploadPhase = 'idle' | 'creating_folders' | 'uploading_files' | 'finalizing'

const FolderTreePreview: React.FC<{ nodes: TreeNode[]; depth?: number }> = ({
  nodes,
  depth = 0,
}) => {
  const { t } = useTranslation()
  return (
    <>
      {nodes.map((node) => (
        <div key={node.tempId}>
          <div
            className="flex items-center py-1"
            style={{ paddingLeft: `${depth * 20}px` }}
          >
            {node.children.length > 0 ? (
              <FolderOpen className="h-4 w-4 mr-2 text-amber-500 shrink-0" />
            ) : (
              <Folder className="h-4 w-4 mr-2 text-amber-500 shrink-0" />
            )}
            <span className="font-medium text-sm">{node.name}</span>
            {node.fileCount > 0 && (
              <span className="text-muted-foreground text-xs ml-2">
                ({node.fileCount} {t('DocControl_files_count')})
              </span>
            )}
          </div>
          {node.children.length > 0 && (
            <FolderTreePreview nodes={node.children} depth={depth + 1} />
          )}
        </div>
      ))}
    </>
  )
}

const DocUploadModal: React.FC<DocUploadModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  folderId,
  documentId,
  initialFolderData,
  initialFiles,
}) => {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<FileWithMeta[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [uploadMode, setUploadMode] = useState<UploadMode>('files')
  const [folderData, setFolderData] = useState<FolderUploadData | null>(null)
  const [uploadPhase, setUploadPhase] = useState<UploadPhase>('idle')

  const isRevisionMode = !!documentId
  const allowMultiple = !isRevisionMode

  const [upload, uploadData] = useUpload() as any

  const [createFile] = useMutation(Mutation.CREATE_FILE)
  const [setFileUploaded] = useMutation(Mutation.SET_FILE_UPLOADED)
  const [uploadRevision] = useMutation(Mutation.UPLOAD_DOC_REVISION)
  const [createFolderTree] = useMutation(Mutation.CREATE_DOC_FOLDER_TREE)

  useEffect(() => {
    if (initialFolderData) {
      setUploadMode('folder')
      setFolderData(initialFolderData)
    } else if (initialFiles && initialFiles.length > 0) {
      setUploadMode('files')
      addFiles(initialFiles)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const addFiles = useCallback(
    (newFiles: FileList | File[]) => {
      const fileArray = Array.from(newFiles)
      const filesToProcess = isRevisionMode ? [fileArray[0]] : fileArray

      const filesToAdd: FileWithMeta[] = filesToProcess.map((file) => ({
        file,
        id: `${file.name}-${Date.now()}-${Math.random()}`,
        name: file.name.replace(/\.[^/.]+$/, ''),
        comment: '',
      }))

      if (isRevisionMode) {
        setFiles(filesToAdd)
      } else {
        setFiles((prev) => [...prev, ...filesToAdd])
      }
    },
    [isRevisionMode],
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      if (!isRevisionMode && hasDirectoryEntries(e.dataTransfer)) {
        const data = await readDroppedItems(e.dataTransfer)
        setUploadMode('folder')
        setFolderData(data)
        return
      }

      const droppedFiles = e.dataTransfer.files
      if (droppedFiles.length > 0) {
        setUploadMode('files')
        addFiles(droppedFiles)
      }
    },
    [addFiles, isRevisionMode],
  )

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        addFiles(e.target.files)
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [addFiles],
  )

  const handleFolderSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const data = readInputFolder(e.target.files)
      setUploadMode('folder')
      setFolderData(data)
    }
    if (folderInputRef.current) {
      folderInputRef.current.value = ''
    }
  }, [])

  const handleRemoveFile = useCallback((fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId))
  }, [])

  const handleNameChange = useCallback((fileId: string, name: string) => {
    setFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, name } : f)))
  }, [])

  const handleCommentChange = useCallback((fileId: string, comment: string) => {
    setFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, comment } : f)))
  }, [])

  const handleClearFolder = useCallback(() => {
    setFolderData(null)
    setUploadMode('files')
  }, [])

  const handleUploadFiles = async () => {
    if (files.length === 0) return

    let successCount = 0
    let errorCount = 0

    try {
      setUploadPhase('uploading_files')
      const filesToUpload: Array<{
        localId: string
        file: File
        name: string
        comment: string
        uploadFileId: string
        signedUrl: string
      }> = []

      for (const fileMeta of files) {
        try {
          const { data } = await createFile({
            variables: {
              input: {
                filename: fileMeta.file.name,
                size: fileMeta.file.size,
                mimetype: fileMeta.file.type || 'application/octet-stream',
                encoding: 'utf8',
                directory: 'doc-control',
              },
            },
          })

          filesToUpload.push({
            localId: fileMeta.id,
            file: fileMeta.file,
            name: fileMeta.name,
            comment: fileMeta.comment,
            uploadFileId: data.createFile.file.id,
            signedUrl: data.createFile.uploadUrl,
          })
        } catch (error: any) {
          console.error('Failed to create file record:', error)
          errorCount++
        }
      }

      if (filesToUpload.length === 0) {
        toastr.error(t('Common_error'), t('DocControl_upload_failed'))
        return
      }

      const uploadPayload = filesToUpload.map((f) => ({
        file: f.file,
        id: f.uploadFileId,
        name: f.file.name,
        signedUrl: f.signedUrl,
      }))

      const uploadedIds = await upload(uploadPayload)
      await setFileUploaded({ variables: { ids: uploadedIds } })

      setUploadPhase('finalizing')
      for (const fileInfo of filesToUpload) {
        try {
          if (isRevisionMode) {
            await uploadRevision({
              variables: {
                fileId: fileInfo.uploadFileId,
                input: {
                  documentId,
                  comment: fileInfo.comment || null,
                },
              },
            })
          } else {
            await uploadRevision({
              variables: {
                fileId: fileInfo.uploadFileId,
                input: {
                  folderId,
                  documentName: fileInfo.name || fileInfo.file.name.replace(/\.[^/.]+$/, ''),
                  comment: fileInfo.comment || null,
                },
              },
            })
          }
          successCount++
        } catch (error: any) {
          console.error('Failed to create document/revision:', error)
          errorCount++
        }
      }
    } catch (error: any) {
      console.error('Upload error:', error)
      toastr.error(t('Common_error'), error.message || t('DocControl_upload_failed'))
      return
    } finally {
      setUploadPhase('idle')
    }

    if (successCount > 0 && errorCount === 0) {
      const messageKey = isRevisionMode
        ? 'DocControl_revision_uploaded'
        : 'DocControl_upload_success'
      toastr.success('', t(messageKey, { count: successCount }))
      setFiles([])
      onComplete()
    } else if (successCount > 0 && errorCount > 0) {
      toastr.warning(
        '',
        t('DocControl_upload_partial', { success: successCount, error: errorCount }),
      )
      setFiles([])
      onComplete()
    } else if (errorCount > 0) {
      toastr.error(t('Common_error'), t('DocControl_upload_failed'))
    }
  }

  const handleUploadFolder = async () => {
    if (!folderData || !folderId) return

    let successCount = 0
    let errorCount = 0

    try {
      setUploadPhase('creating_folders')
      const folderMapping = new Map<string, string>()

      if (folderData.folders.length > 0) {
        const { data } = await createFolderTree({
          variables: {
            rootFolderId: folderId,
            folders: folderData.folders.map((f) => ({
              tempId: f.tempId,
              name: f.name,
              parentTempId: f.parentTempId || null,
            })),
          },
        })

        for (const mapping of data.createDocFolderTree) {
          folderMapping.set(mapping.tempId, mapping.folderId)
        }
      }

      setUploadPhase('uploading_files')
      const filesToUpload: Array<{
        file: File
        uploadFileId: string
        signedUrl: string
        targetFolderId: string
        documentName: string
      }> = []

      for (const fileEntry of folderData.files) {
        try {
          const { data } = await createFile({
            variables: {
              input: {
                filename: fileEntry.file.name,
                size: fileEntry.file.size,
                mimetype: fileEntry.file.type || 'application/octet-stream',
                encoding: 'utf8',
                directory: 'doc-control',
              },
            },
          })

          const targetFolderId = fileEntry.folderTempId
            ? folderMapping.get(fileEntry.folderTempId) || folderId
            : folderId

          filesToUpload.push({
            file: fileEntry.file,
            uploadFileId: data.createFile.file.id,
            signedUrl: data.createFile.uploadUrl,
            targetFolderId,
            documentName: fileEntry.file.name.replace(/\.[^/.]+$/, ''),
          })
        } catch (error: any) {
          console.error('Failed to create file record:', error)
          errorCount++
        }
      }

      if (filesToUpload.length > 0) {
        const uploadPayload = filesToUpload.map((f) => ({
          file: f.file,
          id: f.uploadFileId,
          name: f.file.name,
          signedUrl: f.signedUrl,
        }))

        const uploadedIds = await upload(uploadPayload)
        await setFileUploaded({ variables: { ids: uploadedIds } })

        setUploadPhase('finalizing')
        for (const fileInfo of filesToUpload) {
          try {
            await uploadRevision({
              variables: {
                fileId: fileInfo.uploadFileId,
                input: {
                  folderId: fileInfo.targetFolderId,
                  documentName: fileInfo.documentName,
                  comment: null,
                },
              },
            })
            successCount++
          } catch (error: any) {
            console.error('Failed to create document/revision:', error)
            errorCount++
          }
        }
      }
    } catch (error: any) {
      console.error('Folder upload error:', error)
      toastr.error(t('Common_error'), error.message || t('DocControl_upload_failed'))
      setUploadPhase('idle')
      return
    }

    setUploadPhase('idle')

    if (successCount > 0 && errorCount === 0) {
      toastr.success('', t('DocControl_folder_upload_success', { count: successCount }))
      setFolderData(null)
      onComplete()
    } else if (successCount > 0 && errorCount > 0) {
      toastr.warning(
        '',
        t('DocControl_folder_upload_partial', { success: successCount, error: errorCount }),
      )
      setFolderData(null)
      onComplete()
    } else if (errorCount > 0) {
      toastr.error(t('Common_error'), t('DocControl_upload_failed'))
    }
  }

  const handleUpload = () => {
    if (uploadMode === 'folder') {
      handleUploadFolder()
    } else {
      handleUploadFiles()
    }
  }

  const isProcessing = uploadPhase !== 'idle'
  const canUpload =
    uploadMode === 'folder'
      ? folderData !== null && !uploadData.loading && !isProcessing
      : files.length > 0 && !uploadData.loading && !isProcessing

  const title = isRevisionMode
    ? t('DocControl_upload_new_revision')
    : t('DocControl_upload_documents')

  const folderTree = folderData ? formatFolderTree(folderData) : []
  const folderSupported = isFolderUploadSupported()

  const phaseLabel =
    uploadPhase === 'creating_folders'
      ? t('DocControl_creating_folders')
      : uploadPhase === 'uploading_files'
        ? t('DocControl_uploading_files')
        : uploadPhase === 'finalizing'
          ? t('DocControl_finalizing_documents')
          : null

  return (
    <>
      {uploadMode === 'files' && (
        <ProgressUpload
          uploads={uploadData.uploads}
          open={uploadPhase === 'uploading_files' || uploadPhase === 'finalizing'}
          finalizing={uploadPhase === 'finalizing'}
          uploadingLabel={t('DocControl_uploading_files')}
          finalizingLabel={t('DocControl_finalizing_documents')}
          waitLabel={t('DocControl_upload_please_wait')}
        />
      )}

      {/* Hidden file inputs - outside Dialog to avoid Radix event interference */}
      <input
        ref={fileInputRef}
        type="file"
        multiple={allowMultiple}
        onChange={handleFileSelect}
        className="hidden"
      />
      {folderSupported && !isRevisionMode && (
        <input
          ref={folderInputRef}
          type="file"
          // @ts-ignore - webkitdirectory is not in standard types
          webkitdirectory=""
          onChange={handleFolderSelect}
          className="hidden"
        />
      )}

      <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !isProcessing) onClose() }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-primary" />
              {title}
            </DialogTitle>
          </DialogHeader>

          {/* p-1.5/-m-1.5: overflow-y-auto also clips horizontally, so the
              scroll box needs interior room for the 4px focus ring; the negative
              margin keeps the fields aligned with the dialog padding. */}
          <div className="space-y-4 flex-1 overflow-y-auto min-h-0 p-1.5 -m-1.5">
            {/* Drop Zone */}
            {files.length === 0 && !folderData && (
              <div
                className={cn(
                  'rounded-md border-2 border-dashed p-8 text-center transition-colors',
                  isDragging ? 'border-primary bg-primary/5' : 'border-border',
                )}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <CloudUpload className={cn(
                  'h-10 w-10 mx-auto mb-3',
                  isDragging ? 'text-primary' : 'text-muted-foreground',
                )} />
                <p className="text-sm mb-1">{t('DocControl_drop_files_or_folder')}</p>
                <p className="text-muted-foreground text-xs mb-4">{t('DocControl_or')}</p>
                <div className="flex justify-center gap-2">
                  <Button size="sm" onClick={() => fileInputRef.current?.click()}>
                    {t('DocControl_browse_files')}
                  </Button>
                  {folderSupported && !isRevisionMode && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => folderInputRef.current?.click()}
                    >
                      <Folder className="h-4 w-4" />
                      {t('DocControl_browse_folder')}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Folder Preview / Progress */}
            {uploadMode === 'folder' && folderData && (
              <div className="space-y-3">
                {!isProcessing && (
                  <div className="flex justify-between items-center">
                    <h4 className="text-sm font-medium">{t('DocControl_folder_preview')}</h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive h-7"
                      onClick={handleClearFolder}
                    >
                      <X className="h-3.5 w-3.5" />
                      {t('Common_clear')}
                    </Button>
                  </div>
                )}

                {!isProcessing && (
                  <div className="rounded-md border max-h-[300px] overflow-y-auto p-3">
                    <FolderTreePreview nodes={folderTree} />
                  </div>
                )}

                {isProcessing && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <LoaderCircle className="h-4 w-4 animate-spin text-primary" />
                      <span className="text-sm font-medium">{phaseLabel}</span>
                    </div>
                    {uploadPhase === 'uploading_files' && (
                      <UploadProgressBars uploads={uploadData.uploads} />
                    )}
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  {t('DocControl_folder_summary', {
                    folders: folderData.folders.length,
                    files: folderData.files.length,
                    size: formatBytes(folderData.totalSize),
                  })}
                </p>
              </div>
            )}

            {/* File List */}
            {uploadMode === 'files' && files.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium">
                  {isRevisionMode ? t('DocControl_selected_file') : t('DocControl_selected_files')}{' '}
                  ({files.length})
                </h4>
                <div className="space-y-2">
                  {files.map((fileMeta) => {
                    return (
                      <div key={fileMeta.id} className="rounded-md border p-3">
                        <div className="flex items-start gap-3">
                          <FileTypeIcon filename={fileMeta.file.name} size={20} className="mt-1" />
                          <div className="flex-1 min-w-0 space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                {!isRevisionMode && (
                                  <Input
                                    value={fileMeta.name}
                                    onChange={(e) => handleNameChange(fileMeta.id, e.target.value)}
                                    placeholder={t('DocControl_document_name')}
                                    className="h-8 text-sm mb-1"
                                  />
                                )}
                                <p className="text-xs text-muted-foreground mb-0 truncate">
                                  {fileMeta.file.name} · {formatBytes(fileMeta.file.size)}
                                </p>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-muted-foreground hover:text-destructive shrink-0"
                                onClick={() => handleRemoveFile(fileMeta.id)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                            <Input
                              value={fileMeta.comment}
                              onChange={(e) => handleCommentChange(fileMeta.id, e.target.value)}
                              placeholder={
                                isRevisionMode
                                  ? t('DocControl_revision_comment_placeholder')
                                  : t('DocControl_document_description_optional')
                              }
                              className="h-8 text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose} disabled={uploadData.loading || isProcessing}>
              {t('Common_cancel')}
            </Button>
            <Button onClick={handleUpload} disabled={!canUpload}>
              {isProcessing ? (
                <LoaderCircle className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {uploadMode === 'folder'
                ? t('DocControl_upload')
                : `${t('DocControl_upload')} ${files.length > 0 ? `(${files.length})` : ''}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default DocUploadModal
